import type { DragControllerConfig, DragControllerState } from './types';
import type { Ref } from 'vue';

import { computed, reactive, ref } from 'vue';

import { DRAG_CONSTANTS, HEADER_OFFSET_CONFIG } from './constants';
import { DragState, MenuLevel } from './types';

export function useDragState(config: DragControllerConfig) {
	const { breakpoint = DRAG_CONSTANTS.DEFAULT_BREAKPOINT } = config;

	// Create reactive state object
	const state = reactive<DragControllerState>({
		currentState: DragState.IDLE,
		menuLevel: MenuLevel.CLOSED,
		currentY: 0,
		lastY: 0,
		isScreenBelowBreakpoint: false,
		isDragging: false,
		isInScrollMode: false,
		headerControlledByScroll: false
	});

	// Additional state refs
	const shouldFixOnBottom = ref(false);
	const lastWindowWidth = ref(0);
	const dragLimit = ref(0);

	// Access shared header drag offset state
	const headerDragOffsetY = useState<number>('header-drag-offset-y', () => 0);

	// State getters as computed properties
	const currentState = computed(() => state.currentState);
	const menuLevel = computed(() => state.menuLevel);
	const currentY = computed(() => state.currentY);
	const isDragging = computed(() => state.isDragging);
	const isInScrollMode = computed(() => state.isInScrollMode);
	const isScreenBelowBreakpoint = computed(() => state.isScreenBelowBreakpoint);

	// State transition functions
	function transitionTo(newState: DragState): void {
		state.currentState = newState;
	}

	function setMenuLevel(level: MenuLevel): void {
		state.menuLevel = level;
	}

	function setCurrentY(y: number): void {
		state.currentY = y;
		state.lastY = y;
	}

	function setDragging(dragging: boolean): void {
		state.isDragging = dragging;
		if (dragging) {
			transitionTo(DragState.DRAGGING);
		} else if (state.currentState === DragState.DRAGGING) {
			transitionTo(DragState.IDLE);
		}
	}

	function setScrollMode(inScrollMode: boolean): void {
		state.isInScrollMode = inScrollMode;
		if (inScrollMode) {
			transitionTo(DragState.SCROLLING);
			state.headerControlledByScroll = true;
		} else if (state.currentState === DragState.SCROLLING) {
			transitionTo(DragState.IDLE);
			state.headerControlledByScroll = false;
		}
	}

	function setMomentumScrolling(isActive: boolean): void {
		if (isActive) {
			transitionTo(DragState.MOMENTUM_SCROLLING);
		} else if (state.currentState === DragState.MOMENTUM_SCROLLING) {
			transitionTo(DragState.IDLE);
		}
	}

	function setHeaderControlledByScroll(controlled: boolean): void {
		state.headerControlledByScroll = controlled;
	}

	function updateHeaderOffsetForDrag(y: number): void {
		// Only update header during drag when not in scroll mode and on mobile
		if (
			state.isScreenBelowBreakpoint &&
			state.isDragging &&
			!state.isInScrollMode &&
			!state.headerControlledByScroll
		) {
			// Convert menu position to header offset (0-60px range)
			// Make the calculation more sensitive to small movements
			// Note: We can't access menuPositioning here, so we use a reasonable approximation
			// The header should be fully hidden when reaching approximately half the screen height
			const approximateHalfOpenPosition =
				typeof window !== 'undefined'
					? window.innerHeight *
						HEADER_OFFSET_CONFIG.SCREEN_HEIGHT_RATIO_FOR_HALF_OPEN
					: HEADER_OFFSET_CONFIG.MOBILE_HEIGHT * 8; // Fallback value
			const progressToHalfOpen = Math.min(
				1,
				Math.abs(y) / approximateHalfOpenPosition
			);
			const normalizedValue =
				progressToHalfOpen * HEADER_OFFSET_CONFIG.MAX_OFFSET;

			// Apply smoothing to prevent jittery movements but keep responsiveness
			headerDragOffsetY.value =
				headerDragOffsetY.value * (1 - HEADER_OFFSET_CONFIG.SMOOTHING_FACTOR) +
				normalizedValue * HEADER_OFFSET_CONFIG.SMOOTHING_FACTOR;
		}
		// Additional safeguard: if y is 0 or very small, ensure header is visible
		else if (
			Math.abs(y) <= HEADER_OFFSET_CONFIG.HIDE_THRESHOLD &&
			state.isScreenBelowBreakpoint
		) {
			headerDragOffsetY.value = HEADER_OFFSET_CONFIG.MIN_OFFSET;
		}
	}

	function checkScreenSize(): void {
		if (typeof window === 'undefined') {
			return;
		}

		state.isScreenBelowBreakpoint = window.innerWidth < breakpoint;

		if (window.innerWidth !== lastWindowWidth.value) {
			lastWindowWidth.value = window.innerWidth;

			if (!state.isScreenBelowBreakpoint) {
				resetState();
				// Reset header offset on desktop
				headerDragOffsetY.value = HEADER_OFFSET_CONFIG.MIN_OFFSET;
			}
		}
	}

	function resetState(): void {
		transitionTo(DragState.IDLE);
		setMenuLevel(MenuLevel.CLOSED);
		setCurrentY(HEADER_OFFSET_CONFIG.MIN_OFFSET);
		setDragging(false);
		setScrollMode(false);
		shouldFixOnBottom.value = false;
		headerDragOffsetY.value = HEADER_OFFSET_CONFIG.MIN_OFFSET;
		state.headerControlledByScroll = false;
	}

	function resetScrollModeIfNeeded(): void {
		if (state.isInScrollMode) {
			setScrollMode(false);
		}
	}

	// Can perform actions based on state
	function canPerformDrag(): boolean {
		return (
			state.isScreenBelowBreakpoint &&
			[DragState.IDLE, DragState.DRAGGING].includes(state.currentState)
		);
	}

	function canPerformScroll(): boolean {
		return (
			state.isScreenBelowBreakpoint &&
			[DragState.SCROLLING, DragState.MOMENTUM_SCROLLING].includes(
				state.currentState
			)
		);
	}

	function shouldHandleScrollInstead(): boolean {
		return state.isScreenBelowBreakpoint && state.isInScrollMode;
	}

	return {
		// State
		state: readonly(state),
		shouldFixOnBottom,
		headerDragOffsetY,

		// Computed getters
		currentState,
		menuLevel,
		currentY,
		isDragging,
		isInScrollMode,
		isScreenBelowBreakpoint,

		// State actions
		transitionTo,
		setMenuLevel,
		setCurrentY,
		setDragging,
		setScrollMode,
		setMomentumScrolling,
		setHeaderControlledByScroll,
		resetState,
		resetScrollModeIfNeeded,
		checkScreenSize,
		updateHeaderOffsetForDrag,

		// State queries
		canPerformDrag,
		canPerformScroll,
		shouldHandleScrollInstead
	};
}
